import React from "react";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { Eye, BookHeart, Download } from "lucide-react";

const FREEBIE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/freebies/prayer-journal.pdf`;

/**
 * Visually mirrors Page 2 (Day One · Healing) of the branded Prayer Journal PDF.
 * Rendered inside a dialog so visitors can feel the quality before handing over
 * their email.
 */
export const PrayerJournalPreview = ({ triggerClassName = "" }) => {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="prayer-journal-preview-trigger"
                    className={`inline-flex items-center gap-1.5 text-[12px] font-semibold text-lilac-900 underline-offset-4 hover:underline decoration-gold/60 transition-colors ${triggerClassName}`}
                >
                    <Eye className="w-3.5 h-3.5 text-gold-deep" />
                    Preview a page
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="prayer-journal-preview-dialog"
                className="max-w-[min(560px,92vw)] p-0 overflow-hidden rounded-[28px] border-lilac-200 bg-[#FDFBFD]"
            >
                {/* Page frame */}
                <div className="relative px-8 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 max-h-[85vh] overflow-y-auto">
                    {/* Gold rule at the top */}
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[9px] tracking-[0.28em] uppercase font-bold text-gold-deep">
                            Becoming H.E.R. · Breaking the Cycles
                        </span>
                        <span className="text-[9px] tracking-[0.12em] uppercase text-lilac-700">
                            02 / 07
                        </span>
                    </div>
                    <div className="h-px bg-gold/70 mb-6" />

                    {/* Giant decorative letter */}
                    <div className="relative">
                        <span
                            aria-hidden
                            className="pointer-events-none absolute -right-2 -top-6 md:-top-10 font-display italic text-lilac-200 leading-none select-none"
                            style={{ fontSize: "11rem" }}
                        >
                            H
                        </span>

                        {/* Eyebrow */}
                        <div className="relative eyebrow text-gold-deep">
                            Day One · Healing
                        </div>

                        {/* Title */}
                        <h3 className="relative mt-3 font-display text-[26px] md:text-3xl leading-[1.15] text-lilac-900">
                            Where am I still carrying
                            <br />
                            what was never mine?
                        </h3>

                        {/* Gold divider */}
                        <div className="relative mt-4 h-[2px] w-14 bg-gold rounded-full" />

                        {/* Scripture */}
                        <p className="relative mt-5 text-[13px] italic text-lilac-700 leading-relaxed">
                            "He heals the brokenhearted and binds up their wounds." — Psalm 147:3
                        </p>
                    </div>

                    {/* Reflect · Write · Release */}
                    <div className="mt-7 eyebrow text-gold-deep">
                        Reflect · Write · Release
                    </div>

                    {/* Numbered prompts with writing lines */}
                    <div className="mt-3 space-y-5">
                        {[
                            "Name one wound you have been tending in silence.",
                            "What would it look like to hand this to God today?",
                            "Who — or what — has God already used to begin this healing?",
                        ].map((prompt, i) => (
                            <div key={i}>
                                <p className="text-[13.5px] text-lilac-900">
                                    <span className="font-bold mr-1.5">{i + 1}.</span>
                                    {prompt}
                                </p>
                                <div className="mt-2 space-y-[11px]">
                                    <div className="h-px bg-lilac-200" />
                                    <div className="h-px bg-lilac-200" />
                                    <div className="h-px bg-lilac-200" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Closing whisper */}
                    <p className="mt-7 text-center text-[11px] italic text-gold-deep">
                        Exhale. You are becoming her. — Healing
                    </p>

                    {/* Footer row */}
                    <div className="mt-5 pt-4 border-t border-lilac-200 text-center text-[10px] text-lilac-700/80 italic">
                        walkwithpurposeco.site · Real conversations · Raw healing · Kingdom transformation
                    </div>
                </div>

                {/* Sticky action strip */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-8 py-5 border-t border-lilac-200 bg-white">
                    <p className="text-[12px] text-lilac-700 text-center sm:text-left">
                        <BookHeart className="inline w-3.5 h-3.5 mr-1 text-gold-deep" />
                        Full 7-page PDF sent to the first 100 women in.
                    </p>
                    <a
                        href={FREEBIE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="prayer-journal-preview-download"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold hover:bg-gold-deep text-white text-[12px] font-semibold shadow-[0_8px_22px_rgba(212,175,55,0.35)] transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Download full PDF
                    </a>
                </div>
            </DialogContent>
        </Dialog>
    );
};
